/**
 * Judgments persist in this browser (IndexedDB, keyed by cutoff and item) and
 * survive a refresh. They leave the browser only through Export scores.
 */
import { create } from 'zustand'
import { db } from '../store/db.js'
import type { Judgment, ScoreField } from './fields.js'

export type ScoresState = {
  readonly cutoff: string | null
  readonly scores: ReadonlyMap<string, Judgment>
  readonly hydrated: boolean
  load(cutoff: string): Promise<void>
  score(item_id: string, field: ScoreField, value: string): Promise<void>
}

export const useScores = create<ScoresState>((set, get) => ({
  cutoff: null,
  scores: new Map(),
  hydrated: false,

  async load(cutoff) {
    const rows = await db().scores.where('cutoff').equals(cutoff).toArray()
    set({ cutoff, scores: new Map(rows.map((r) => [r.item_id, r.judgment])), hydrated: true })
  },

  async score(item_id, field, value) {
    const { cutoff, scores } = get()
    if (cutoff === null) return
    const previous = scores.get(item_id) ?? {}
    const kept = Object.fromEntries(
      Object.entries(previous).filter(([k]) => k !== field && k !== 'updated_at'),
    )
    const next: Judgment = {
      ...kept,
      ...(value === '' ? {} : { [field]: value }),
      updated_at: new Date().toISOString(),
    }
    const map = new Map(scores)
    map.set(item_id, next)
    set({ scores: map })
    await db().scores.put({ key: `${cutoff}|${item_id}`, cutoff, item_id, judgment: next })
  },
}))
